import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Check, ChevronDown, ArrowRight, Leaf, Shield, Clock, Droplets } from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

export default function KebonyKled() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('clear');

  const stats = [
    { value: '30+', label: 'År garanti' },
    { value: '3×', label: 'Hardere enn ubehandlet furu' },
    { value: '50%', label: 'Reduksjon i vannopptak' },
    { value: '100%', label: 'Fornybar teknologi' },
  ];

  const process = [
    { step: '01', title: 'Valg av Trevirke', desc: 'Kun FSC™-sertifisert furu (Pinus sylvestris) brukes. Trevirket selekteres nøye for optimal tetthet og struktur.' },
    { step: '02', title: 'Furfurylering', desc: 'Trevirket impregneres med bio-basert furfurylalkohol under trykk. Alkoholen polymeriserer inne i treet.' },
    { step: '03', title: 'Herding', desc: 'Varmebehandling aktiverer de kjemiske bindingene. Trevirket blir permanent modifisert — ikke bare overflatebehandlet.' },
    { step: '04', title: 'Kvalitetskontroll', desc: 'Hvert brett kontrolleres for dimensjoner, farge og mekaniske egenskaper. Kun godkjente produkter forlater fabrikken.' },
  ];

  const clearImages = [
    'https://kebonypolska.pl/wp-content/uploads/2023/08/kebony_clear_new1_rotated.webp',
    'https://kebonypolska.pl/wp-content/uploads/2023/08/kebony_clear_weathe_rotated.webp',
  ];

  const faqItems = [
    { q: 'Hva skiller Kebony fra ordinær trykkimpregnert furu?', a: 'Trykkimpregnert furu bruker kobber og andre tungmetaller som kan lekke ut. Kebony bruker bio-basert furfurylalkohol som binder seg permanent til trestrukturen — ingen giftige stoffer, ingen utlekking, og bedre mekaniske egenskaper.' },
    { q: 'Trenger jeg å olje eller behandle Kebony-kledning?', a: 'Nei. Kebony krever ingen overflatebehandling for å opprettholde sine strukturelle egenskaper. Den naturlige sølvgrå patinaen er en estetisk prosess, ikke et tegn på forfall. Ønsker du å bevare den opprinnelige brune fargen, kan du bruke Kebony-olje én gang i året.' },
    { q: 'Hvordan monteres Kebony-kledning?', a: 'Kebony kledning kan monteres som vanlig trekledning — med synlige eller skjulte fester. Bruk rustfrie skruer eller stål som ikke reagerer med Kebony. Husk å predrill ved endekorn for å unngå sprekker.' },
    { q: 'Kan Kebony brukes i kystnære strøk og saltluft?', a: 'Ja. Kebony er spesielt godt egnet for eksponerte miljøer — sjø, fjell og kyst. Den høye motstanden mot fuktopptak og råte gjør den ideell for norske klimaforhold med mye nedbør og temperatursvingninger.' },
    { q: 'Hva er forskjellen mellom Kebony Clear og Kebony Character?', a: 'Kebony Clear er produsert av kjerneved-fri furu med glatt overflate og jevn farge — mer eksklusivt utseende. Kebony Character har naturlige kvistar og varierende tekstur, noe som gir et mer rustikt, organisk uttrykk til lavere pris.' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <div className="relative h-[100vh] min-h-[600px] overflow-hidden">
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/80e7bbb55_Mareiner-Kebony-BODEN-GEHOBELT-2374_w.jpg"
          alt="Kebony kledning badstue"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        <div className="absolute inset-0 flex flex-col justify-end pb-24 px-8 md:px-20 max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-base tracking-widest-2xl font-body uppercase text-white/60 mb-4">Naturlig Norsk Teknologi</p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              Kebony<br /><span className="italic">Kledning</span>
            </h1>
            <p className="font-body text-white/70 text-2xl max-w-2xl leading-relaxed mb-10">
              Tre som modnes over tid. Norsk teknologi for tremodifisering — holdbarheten av tropisk treverk uten miljøkostnaden.
            </p>
            <div className="flex flex-wrap gap-4 mb-32 md:mb-0">
              <motion.a whileHover={{ scale: 1.04 }} href="/kebony-vedlikehold"
                className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-flex items-center gap-2">
                VEDLIKEHOLDSGUIDE <ArrowRight size={16} />
              </motion.a>
              <motion.a whileHover={{ scale: 1.04 }} href="/be-om-tilbud"
                className="px-8 py-4 border border-white/40 text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/10 transition-all">
                BE OM TILBUD
              </motion.a>
            </div>
          </motion.div>
        </div>
        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-6xl mx-auto px-8 md:px-20 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-light text-white">{s.value}</p>
                <p className="font-body text-xs text-white/50 tracking-widest uppercase mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-20">

        {/* INTRO */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-28">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div>
              <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Hva er Kebony?</p>
              <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">
                Tre som <span className="italic">eldes</span><br />med verdighet
              </h2>
              <p className="font-body text-foreground/70 leading-relaxed mb-6 text-2xl">
                Kebony er en norsk patentert teknologi som modifiserer bærekraftig furu ved å impregnere det med bio-basert furfurylalkohol — et naturlig biprodukt fra landbruksavfall. Resultatet er et tre med egenskaper som ligner tropiske tresorter, men uten miljøkostnaden.
              </p>
              <p className="font-body text-foreground/70 leading-relaxed mb-10 text-2xl">
                Kebony Clear Kledning (KLED) er den klareste varianten — produsert av kjerneved-fri furu med glatt, konsistent overflate. Perfekt for fasadekledning på badstuer, hytter og bolighus som skal stå i generasjoner.
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">

              </div>
            </div>
          </div>
        </motion.section>

      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-20">

        {/* PATINA SECTION */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-28">
          <div className="bg-[#2a1810] rounded-[8px] p-10 md:p-16 grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-base tracking-widest-2xl font-body uppercase text-white/40 mb-6">Naturlig aldring</p>
              <h2 className="font-heading text-4xl font-light leading-[1.1] mb-8 text-white">
                Sølvgrå patina —<br /><span className="italic">skjønnhet som vokser</span>
              </h2>
              <p className="font-body text-white/70 leading-relaxed mb-6 text-2xl">
               Kebony starter som varm, mørk brun. Over måneder og år dannes en naturlig sølvgrå patina — resultatet av UV-lys og fuktighet. Dette er ikke et tegn på forfall, men Kebonys mest unike egenskap.
              </p>
              <p className="font-body text-white/70 leading-relaxed mb-8 text-2xl">
               Patineringsprosessen varierer avhengig av eksponering mot sol og nedbør. Nordvendte flater holder seg mørkere lenger enn sydvendte.
              </p>
              <div className="flex gap-8">
                <div>
                  <div className="w-12 h-12 rounded-full mb-3" style={{ backgroundColor: '#5c3a1e' }}></div>
                  <p className="font-body text-xs text-white/50 uppercase tracking-widest">Ny (0–6 mnd)</p>
                </div>
                <div>
                  <div className="w-12 h-12 rounded-full mb-3" style={{ backgroundColor: '#8b7355' }}></div>
                  <p className="font-body text-xs text-white/50 uppercase tracking-widest">1–2 år</p>
                </div>
                <div>
                  <div className="w-12 h-12 rounded-full mb-3" style={{ backgroundColor: '#a8a8a0' }}></div>
                  <p className="font-body text-xs text-white/50 uppercase tracking-widest">3+ år (patina)</p>
                </div>
              </div>
            </div>
            <div>
              <p className="font-body text-xs text-white/40 uppercase tracking-widest mb-4 text-center">Dra suvaken for å sammenligne</p>
              <BeforeAfterSlider
                beforeSrc="https://kebonypolska.pl/wp-content/uploads/2023/08/kebony_clear_new1_rotated.webp"
                afterSrc="https://kebonypolska.pl/wp-content/uploads/2023/08/kebony_clear_weathe_rotated.webp"
                beforeLabel="Ny — mørk brun"
                afterLabel="Patina — sølvgrå"
                height={360}
              />
            </div>
          </div>
        </motion.section>

        {/* CLEAR vs CHARACTER */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="py-28">
          <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Produktvarianter</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-12 text-[#2a1810]">
            Clear vs. <span className="italic">Character</span>
          </h2>
          <div className="flex gap-1 mb-12 border border-border/30 rounded-[6px] p-1 w-fit">
            {['clear', 'character'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-8 py-3 rounded-[4px] font-body text-sm tracking-widest uppercase transition-all ${activeTab === tab ? 'bg-[#2a1810] text-white' : 'text-foreground/60 hover:text-foreground'}`}>
                Kebony {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === 'clear' ? (
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <h3 className="font-heading text-3xl font-light mb-6 text-[#2a1810]">Kebony Clear Kledning</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-8 text-2xl">
                  Produsert av kjerneved-fri furu (Pinus sylvestris) med FSC™-sertifikat. Glatt overflate uten synlige kvistar gir et eksklusivt, moderne uttrykk. Perfekt for badstuer og bolighus der design er viktig.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { label: 'Overflate', value: 'Glatt, kvistfri' },
                    { label: 'Trevirke', value: 'Pinus sylvestris (FSC™)' },
                    { label: 'Garanti', value: '30 år' },
                    { label: 'Dimensjoner', value: '19×120 mm / 19×145 mm / 22×145 mm' },
                    { label: 'Profiler', value: 'T&G 45°, T&G 75°, rektangulær, potrójna listwa' },
                    { label: 'Farge (ny)', value: 'Mørk brun (#5c3a1e)' },
                    { label: 'Farge (patina)', value: 'Sølvgrå — naturlig prosess' },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between border-b border-border/20 pb-3">
                      <span className="font-body text-2xl text-foreground/50">{r.label}</span>
                      <span className="font-body text-2xl text-[#2a1810]">{r.value}</span>
                    </div>
                  ))}
                  </div>
                  </div>
                  <div className="space-y-4">
                  <div className="rounded-[8px] overflow-hidden h-80">
                  <img src="https://kebonypolska.pl/wp-content/uploads/2024/06/W-2887-Product_photo-short.jpg_747x603-300x300.webp" alt="Kebony Clear deska elewacyjna" className="w-full h-full object-cover" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-[8px] overflow-hidden h-44">
                    <img src="https://kebonypolska.pl/wp-content/uploads/2024/06/W-2878-Product_photo-short-300x300.png" alt="Kebony T&G profil" className="w-full h-full object-cover bg-secondary/30" />
                  </div>
                  <div className="rounded-[8px] overflow-hidden h-44">
                    <img src="https://kebonypolska.pl/wp-content/uploads/2024/06/W-2888-Product_photo-short-300x300.png" alt="Kebony listwa" className="w-full h-full object-cover bg-secondary/30" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <h3 className="font-heading text-3xl font-light mb-6 text-[#2a1810]">Kebony Character Kledning</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-8 text-2xl">
                  Kebony Character har naturlige kvistar, varierende tekstur og et mer rustikt, organisk utseende. Lavere pris enn Clear, men samme teknologi og holdbarhet. Ideell for hytter og tradisjonelle fasader.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { label: 'Overflate', value: 'Naturlig — med kvistar' },
                    { label: 'Trevirke', value: 'Pinus sylvestris (FSC™)' },
                    { label: 'Garanti', value: '30 år' },
                    { label: 'Dimensjoner', value: '21×148 mm (Shiplap)' },
                    { label: 'Profiler', value: 'Shiplap 75 1SL, Shiplap standard' },
                    { label: 'Uttrykk', value: 'Rustikt, organisk' },
                    { label: 'Pris', value: 'Lavere enn Clear' },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between border-b border-border/20 pb-3">
                      <span className="font-body text-2xl text-foreground/50">{r.label}</span>
                      <span className="font-body text-2xl text-[#2a1810]">{r.value}</span>
                    </div>
                  ))}
                  </div>
                  </div>
                  <div className="space-y-4">
                  <div className="rounded-[8px] overflow-hidden h-80">
                  <img src="https://kebonypolska.pl/wp-content/uploads/2024/06/W-2596-Product_photo-short-300x300.jpg" alt="Kebony Character shiplap" className="w-full h-full object-cover" />
                  </div>
                <div className="rounded-[8px] overflow-hidden h-44">
                  <img src="https://kebonypolska.pl/wp-content/uploads/2024/06/W-2242-Product_photo-short.jpg_747x603-300x300.webp" alt="Kebony Character" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          )}
        </motion.section>

        {/* SAMMENLIGNING */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="pb-28">
          <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Sammenligning</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">
            Kebony vs. <span className="italic">Alternativene</span>
          </h2>
          <div className="overflow-x-auto rounded-[8px] border border-border/20 custom-scrollbar">
            <table className="w-full">
              <thead className="bg-[#2a1810] text-white">
                <tr>
                  {['Egenskap', 'Kebony Clear', 'Tropisk teak', 'Trykkimpregnert furu', 'Kompositt'].map((h, i) => (
                    <th key={i} className="text-left py-4 px-5 font-body text-xs tracking-widest uppercase font-normal opacity-80">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-border/20">
                {[
                  ['Garanti', '30 år', '~15 år', '~10 år', '25 år'],
                  ['Vedlikehold', 'Minimalt', 'Regelmessig olje', 'Maling/beis', 'Ingen'],
                  ['Bærekraft', '✓ FSC™', '✗ Avskoging', '✗ Kjemisk', '✗ Plast'],
                  ['Bearbeidbarhet', 'Som hardtre', 'Vanskelig', 'Enkel', 'Begrenset'],
                  ['Naturlig utseende', '✓ Ekte tre', '✓ Ekte tre', '✓ Ekte tre', '✗ Kunstig'],
                  ['Holdbarhet (fukt)', 'Meget god', 'Meget god', 'God', 'Meget god'],
                ].map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={`py-4 px-5 font-body text-2xl ${j === 0 ? 'font-light text-[#2a1810]' : j === 1 ? 'text-[#2d5a3d] font-medium' : 'text-foreground/60'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="pb-28">
          <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Spørsmål & Svar</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">
            Vanlige <span className="italic">Spørsmål</span>
          </h2>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="border border-border/20 rounded-[6px] overflow-hidden hover:border-[#2d5a3d] transition-colors">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 bg-white hover:bg-secondary/30 transition-colors text-left"
                >
                  <h3 className="font-heading text-2xl font-light pr-4 text-[#2a1810]">{item.q}</h3>
                  <ChevronDown size={20} className={`flex-shrink-0 text-primary transition-transform duration-200 ${expandedFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="bg-secondary/20 border-t border-border/20 px-6 py-5">
                    <p className="font-body text-foreground/70 leading-relaxed text-2xl">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="pb-28">
          <div className="bg-gradient-to-br from-[#2d5a3d] to-[#1a3a28] text-white rounded-[6px] p-12 md:p-20 text-center">
            <p className="text-base tracking-widest-2xl font-body uppercase text-white/50 mb-6">Din Badstue Venter</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-6 text-white">
              Klar for Kebony<br /><span className="italic">på din badstue?</span>
            </h2>
            <p className="font-body text-white/70 text-2xl mb-12 max-w-2xl mx-auto">
              Alle våre badstuer er tilgjengelige med Kebony Clear-kledning. Kontakt oss for å diskutere din drømmebadstue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a whileHover={{ scale: 1.04 }} href="/be-om-tilbud"
                className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block">
                BE OM TILBUD
              </motion.a>
              <motion.a whileHover={{ scale: 1.04 }} href="/kebony-vedlikehold"
                className="px-8 py-4 border border-white/30 text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/10 transition-all inline-block">
                VEDLIKEHOLDSGUIDE
              </motion.a>
            </div>
          </div>
        </motion.section>

      </div>

      <Footer />
    </div>
  );
}